import { forwardRef, memo, useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
// Material UI imports
import Button, { type ButtonProps } from '@mui/material/Button';
// SCSS imports
import { button, outlined } from './ScopeSelectorButton.module.scss';

type ScopeSelectorButtonProps = {
  readonly className?: string;
  readonly color?: 'inherit' | 'primary' | 'secondary' | 'info'; // TODO: use MUI color type
  readonly simple?: boolean;
  readonly item: string;
  readonly selected: boolean;
  readonly disabled?: boolean;
  readonly onChange: (item: string) => void;
  readonly sx?: ButtonProps['sx'];
};

const ScopeSelectorButton = forwardRef<HTMLButtonElement, ScopeSelectorButtonProps>(({
  simple = false,
  item,
  selected,
  disabled,
  onChange,
  className,
  color,
  ...props
}, ref) => {
  const { formatMessage } = useIntl();
  const label = formatMessage({ id: `scopeselector.${item}` });

  const handleClick = useCallback(() => onChange(item), [item, onChange]);

  return (
    <Button
        ref={ref}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        aria-label={label}
        aria-pressed={selected}
        color={selected ? (simple && 'secondary') || color || 'primary' : 'info'}
        variant={selected ? 'contained' : 'outlined'}
        disabled={disabled}
        disableElevation
        onClick={handleClick}
        className={clsx(className, button, {
          [outlined]: !selected
        })}
    >
      {label}
    </Button>
  );
});

ScopeSelectorButton.displayName = 'ScopeSelectorButton';

export default memo(ScopeSelectorButton);
